// Countries GaintPlay is closed in (Admin → Countries, "Blocked"). When the visitor is there, or a
// signed-in member's account is from there (the API answers COUNTRY_BLOCKED), this notice takes the
// place of the whole site, with the one thing still open: writing to support by email. Until the
// answer arrives, or if it can't be read, the site opens as usual; the API refuses on its own.
import { useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe2 } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { meQuery } from '../lib/session';
import { countryQuery } from '../lib/queries';
import { hideLaunchSplash } from '../lib/splash';
import { CoinMascot } from './Coin';
import { getLocale, T, t } from '../i18n';

const SUPPORT_EMAIL = 'support@gaintplay.com';

export function CountryGate({ children }: { children: ReactNode }) {
  const { data } = useQuery(countryQuery);
  // The session as the rest of the site reads it. Only when the country is closed does this ask
  // for it itself (the site isn't there to): a member gets "Log out" on the notice.
  const me = useQuery({ ...meQuery, enabled: data?.access === 'blocked' });
  const refused = me.error instanceof ApiError && me.error.code === 'COUNTRY_BLOCKED';
  const member = refused || !!me.data;
  const blocked = data?.access === 'blocked' || refused;
  if (!blocked) return <>{children}</>;
  return (
    <BlockedScreen country={data?.access === 'blocked' ? data.country : null} member={member} />
  );
}

function countryName(code: string | null): string | null {
  if (!code) return null;
  try {
    return new Intl.DisplayNames([getLocale()], { type: 'region' }).of(code) ?? null;
  } catch {
    return null;
  }
}

function BlockedScreen({ country, member }: { country: string | null; member: boolean }) {
  const qc = useQueryClient();
  const name = countryName(country);
  useEffect(() => {
    // i18n: the browser tab's title when GaintPlay is closed in the visitor's country
    document.title = t('Not available in your country');
    hideLaunchSplash();
  }, []);
  const logout = async () => {
    await api('/auth/logout', { body: {} }).catch(() => {});
    qc.clear();
    window.location.assign('/');
  };
  return (
    <main
      aria-labelledby="country-closed-title"
      className="flex min-h-dvh flex-col bg-canvas px-6 pb-safe pt-safe"
    >
      <div className="mx-auto flex w-full max-w-[420px] flex-1 flex-col">
        <div className="flex flex-1 flex-col justify-center pb-8 pt-12">
          <CoinMascot size={104} rotate={-12} className="-ml-1" />
          {name && (
            <p className="eyebrow mt-8 flex items-center gap-1.5">
              <Globe2 aria-hidden="true" size={14} strokeWidth={2} />
              {name}
            </p>
          )}
          <h1
            id="country-closed-title"
            className={`large-title text-ink ${name ? 'mt-2' : 'mt-8'}`}
          >
            {t('GaintPlay isn’t available in your country')}
          </h1>
          <p className="mt-3 text-pretty text-[16px] leading-relaxed text-graphite">
            {t(
              'We can’t offer GaintPlay where you are right now, so signing up, earning and cashing out are closed here.',
            )}
          </p>
          <div className="card mt-6 p-4">
            <p className="text-[15px] leading-snug text-graphite">
              <T
                msg="<b>Questions about your account?</b> Write to {email} and we’ll answer there."
                values={{ email: SUPPORT_EMAIL }}
                tags={{ b: (c) => <span className="font-semibold text-ink">{c}</span> }}
              />
            </p>
          </div>
        </div>
        <div className="pb-6">
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="btn-primary pressable min-h-[52px] w-full text-[16px]"
          >
            {
              // i18n: button that opens the email app to write to support
              t('Email support')
            }
          </a>
          {member && (
            <button
              type="button"
              onClick={() => void logout()}
              className="btn-ghost pressable mt-3 min-h-[52px] w-full text-[16px]"
            >
              {t('Log out')}
            </button>
          )}
        </div>
      </div>
    </main>
  );
}
